/* Cached values whose identity JSON does not preserve.
 *
 * A generated message is a plain object, which is why a JSON round trip works on one at
 * all — but not entirely plain: a 64-bit field is a `bigint` and a bytes field is a
 * `Uint8Array`. `JSON.stringify` *throws* on the first (every `Timestamp.seconds` in the
 * tree, so most of the console's reads) and silently turns the second into `{"0":1,…}`,
 * which is worse: it restores as an object that every consumer treats as bytes.
 *
 * Decoded Dates also become strings. All three are tagged and rebuilt at this boundary. The
 * tags are escaped when they occur literally in arbitrary JSON tool data. An escaped
 * object carries entries rather than another object, so its literal keys are rebuilt
 * only after the reviver has visited its children. This includes the escape tag itself. */
const BIGINT_TAG = "$sxBigInt";
const BYTES_TAG = "$sxBytes";
const DATE_TAG = "$sxDate";
const OBJECT_TAG = "$sxObject";
const ERROR_TAG = "$sxError";
const TYPE_TAGS = new Set([BIGINT_TAG, BYTES_TAG, DATE_TAG, OBJECT_TAG, ERROR_TAG]);

const toBase64 = (bytes: Uint8Array): string => {
  let characters = "";
  for (const byte of bytes) characters += String.fromCharCode(byte);
  return btoa(characters);
};

const fromBase64 = (text: string): Uint8Array =>
  Uint8Array.from(atob(text), (character) => character.charCodeAt(0));

interface ErrorCodec {
  readonly encode: (error: Error) => unknown;
  readonly decode: (value: unknown) => Error;
}

/** The disk cache uses only value types; request transfer adds public error types. */
export function createCacheCodec(errors?: ErrorCodec) {
  function tagged(
    this: Record<string, unknown>,
    key: string,
    value: unknown,
  ): unknown {
    // Date.toJSON runs before a replacer. Read the original holder to preserve the type,
    // without guessing whether an ordinary ISO-shaped string represents a Date.
    const original = this[key];
    if (original instanceof Error && errors) return { [ERROR_TAG]: errors.encode(original) };
    if (original instanceof Date) return { [DATE_TAG]: original.toISOString() };
    if (typeof value === "bigint") return { [BIGINT_TAG]: value.toString() };
    if (value instanceof Uint8Array) return { [BYTES_TAG]: toBase64(value) };
    if (typeof value === "object" && value !== null && !Array.isArray(value)) {
      const keys = Object.keys(value);
      if (keys.length === 1 && TYPE_TAGS.has(keys[0])) {
        return { [OBJECT_TAG]: Object.entries(value) };
      }
    }
    return value;
  }

  function untagged(_key: string, value: unknown): unknown {
    if (typeof value !== "object" || value === null) return value;
    const record = value as Record<string, unknown>;
    const keys = Object.keys(record);
    if (keys.length !== 1) return value;
    if (keys[0] === ERROR_TAG && errors) return errors.decode(record[ERROR_TAG]);
    if (keys[0] === OBJECT_TAG) {
      const entries: unknown = record[OBJECT_TAG];
      if (!Array.isArray(entries) || !entries.every(
        (entry: unknown): entry is [string, unknown] =>
          Array.isArray(entry) && entry.length === 2 && typeof entry[0] === "string",
      )) throw new Error("Invalid cached object");
      return Object.fromEntries(entries);
    }
    if (keys[0] === DATE_TAG && typeof record[DATE_TAG] === "string") {
      const date = new Date(record[DATE_TAG]);
      if (!Number.isFinite(date.getTime()))
        throw new Error("Invalid cached date");
      return date;
    }
    if (keys[0] === BIGINT_TAG && typeof record[BIGINT_TAG] === "string") {
      return BigInt(record[BIGINT_TAG]);
    }
    if (keys[0] === BYTES_TAG && typeof record[BYTES_TAG] === "string") {
      return fromBase64(record[BYTES_TAG]);
    }
    return value;
  }

  return {
    stringify: (value: unknown): string => JSON.stringify(value, tagged),
    parse: (value: string): unknown => JSON.parse(value, untagged),
  };
}
